import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Card, CardBody, Col, Row } from "reactstrap";
import { getJobsApplications } from "../../../../actions/employer";
import {
  getCompanyLogo,
  getJobLocationById,
  getUserPic,
} from "../../../../helpers/common";
import { RejectButton } from "./RejectButton";
import { ShortListButton } from "./ShortListButton";
import { useNavigate } from "react-router-dom";
// inside your component:
const ApplicationItem = ({ data }) => {
  const customer = useSelector((state) => state.customer.profile);
  const locations = useSelector((state) => state.jobLocations.data) || [];
   const navigate = useNavigate();
  const dispatch = useDispatch();

  const invalidateApi = () => {
    dispatch(getJobsApplications(customer.id));
  };

  const jobDetails = data?.job;

  // Getting company details
  const companyDetails = data?.company;

  const candidateDetails = data?.candidate;
  const candidateAdditionalDetails = data?.candidate_additional_fields;

  // Application Status
  const applicationStatus = data?.status;

  // Candidate detail page URL
  const candidateDetailUrl = `/candidate/details/${candidateDetails?.id}`;

  // Job detail page URL
  const jobDetailsUrl = `/job/details/${jobDetails?.id}/${jobDetails.url_slug}`;

  // 🟢 NEW: correct field names per the actual API response.
  // If the candidate has applied to a job (isJobApplicant === true), their contact info
  // should always be visible, regardless of isContactLocked.
  const shouldShowContact =
    candidateDetails?.isJobApplicant === true ||
    candidateDetails?.isContactLocked === false;

  const candidateEmail = candidateDetails?.email;
  const candidatePhone =
    candidateDetails?.mobileNumber || candidateDetails?.phoneNumber;

  return (
    <>
    <Card 
  className="job-box card mt-4" 
  onClick={(e) => {
    // Prevent card click when clicking interactive elements, buttons, or links inside it
    if (e.target.closest("a, button, .btn, .badge, input, select, textarea")) {
      return;
    }
    navigate(candidateDetailUrl);
  }}
  style={{ cursor: "pointer" }}
>
  <CardBody className="p-4">
    <Row className="align-items-start">
      <Col lg={1} className="text-center text-lg-start">
        {(() => {
          const photoName =
            candidateDetails?.candidate?.photo ||
            candidateDetails?.photo ||
            candidateDetails?.data?.photo;

          const generatedUrl = getUserPic(photoName);

          return (
            <Link to={candidateDetailUrl}>
              <img
                src={generatedUrl}
                alt="Profile"
                className="avatar-md img-thumbnail rounded-circle border border-2 border-light shadow-sm"
              />
            </Link>
          );
        })()}
      </Col>

      <Col lg={8}>
        <div className="mt-3 mt-lg-0 ">
          <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
            <h5 className="fs-17 mb-0 fw-semibold">
              <Link to={candidateDetailUrl} className="text-dark text-decoration-none">
                {candidateDetails?.name || "Unknown"}
              </Link>
            </h5>

            <span
              className={`badge rounded-pill px-2 py-1 fs-12 fw-medium ${applicationStatus === "Pending"
                ? "bg-warning-subtle text-warning border border-warning-subtle"
                : applicationStatus === "Shortlisted"
                  ? "bg-success-subtle text-success border border-success-subtle"
                  : "bg-danger-subtle text-danger border border-danger-subtle"
                }`}
            >
              <i
                className={`mdi ${applicationStatus === "Pending"
                  ? "mdi-clock-outline"
                  : applicationStatus === "Shortlisted"
                    ? "mdi-check-circle-outline"
                    : "mdi-close-circle-outline"
                  } me-1`}
              ></i>
              {applicationStatus}
            </span>
          </div>

          {/* 🎯 Applied for + date — compact pill-style info chip */}
          <div className="d-inline-flex align-items-center bg-light rounded-pill px-3 py-1 mb-2">
            <i className="mdi mdi-briefcase-outline text-primary me-1 fs-15"></i>
            <span className="fs-13 text-muted">
              Applied for{" "}
              <span className="fw-semibold text-dark">
                {jobDetails?.job_title || "N/A"}
              </span>
              {data?.createdAt && (
                <>
                  <span className="mx-2 text-muted">|</span>
                  <i className="mdi mdi-calendar-outline me-1"></i>
                  {new Date(data.createdAt).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </>
              )}
            </span>
          </div>

          <ul className="list-inline mb-0 text-muted">
            {candidateAdditionalDetails?.designation && (
              <li className="list-inline-item me-3">
                <i className="uil uil-bag text-primary me-1"></i>
                {candidateAdditionalDetails?.designation}
              </li>
            )}

            <li className="list-inline-item me-3">
              <i className="mdi mdi-map-marker text-primary"></i>{" "}
              {data?.location
                ? `${data.location.name}, ${data.location.state}`
                : "N/A"}
            </li>
            {candidateAdditionalDetails?.expected_salary_per_month && (
              <li className="list-inline-item me-3">
                <i className="uil uil-wallet text-primary me-1"></i>₹
                {candidateAdditionalDetails?.expected_salary_per_month} /
                month
              </li>
            )}

            {/* 🟢 NEW: email and phone — shown when candidate is a job applicant,
            bypassing the credit-based contact lock */}
            {shouldShowContact && candidateEmail && (
              <li className="list-inline-item me-3">
                <i className="mdi mdi-email-outline text-primary me-1"></i>
                {candidateEmail}
              </li>
            )}
            {shouldShowContact && candidatePhone && (
              <li className="list-inline-item me-3">
                <i className="mdi mdi-phone text-primary me-1"></i>
                {candidatePhone}
              </li>
            )}
          </ul>
        </div>
      </Col>

      {applicationStatus === "Pending" && (
        <Col
          lg={3}
          className="d-flex justify-content-lg-end align-items-center mt-3 mt-lg-0"
        >
          <li
            className="list-inline-item position-relative me-2"
            title="Reject"
          >
            <RejectButton data={data} invalidateApi={invalidateApi} />
          </li>

          <li
            className="list-inline-item position-relative"
            title="Post Jobs"
          >
            <ShortListButton data={data} invalidateApi={invalidateApi} />
          </li>
        </Col>
      )}
    </Row>

    {data?.message && (
      <>
        <div className="cust-divider"></div>

        <div className="bg-light rounded-3 p-3 mt-2">
          <p className="text-muted mb-0" style={{ fontSize: 14 }}>
            <i className="mdi mdi-message-text-outline text-primary me-1"></i>
            <span className="text-black fw-medium">Candidate Message:</span>{" "}
            {data?.message}
          </p>
        </div>
      </>
    )}

    {/* 🎯 Rejection Reason & Date Display */}
    {data?.rejectionReason && (
      <div className="bg-danger-subtle rounded-3 p-3 mt-2 border border-danger-subtle">
        <p className="text-danger mb-0" style={{ fontSize: 14 }}>
          <i className="mdi mdi-close-circle-outline me-1"></i>
          <span className="fw-semibold">Rejection Reason:</span>{" "}
          {data.rejectionReason}
          {(data?.updatedAt || data?.createdAt) && (
            <span className="float-end text-muted fs-12">
              <i className="mdi mdi-calendar-outline me-1"></i>
              {new Date(data.updatedAt || data.createdAt).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </span>
          )}
        </p>
      </div>
    )}
  </CardBody>
</Card>
    </>
  );
};

export default ApplicationItem;
